import { Injectable, NgZone, inject } from '@angular/core';

import { environment } from '@envs/environment';

declare var PNM: any;

export interface PnmPaymentTypes {
    credit?: boolean;
    debit?: boolean;
    ach?: boolean;
}

export interface PnmInitConfig {
    orderToken: string;
    target: string;
    callback: (response: any) => void;
    action: { name: string; type: 'tokenize' };
    paymentTypes?: PnmPaymentTypes;
    buttonText?: string;
    language?: string;
}

const SCRIPT_LOAD_TIMEOUT_MS = 15000;
const PNM_READY_TIMEOUT_MS = 5000;
const PNM_READY_POLL_MS = 100;

/**
 * Wrapper around the PayNearMe embedded SDK, ported from sign-portal so the ROSE Portal
 * card form is the same component the applicant already met during signing (OPS-2028).
 *
 * PNM callbacks fire outside Angular's zone, so every one is re-entered through
 * {@link NgZone.run} — otherwise a successful tokenize would not repaint the modal.
 */
@Injectable({ providedIn: 'root' })
export class PayNearMeService {

    readonly #ngZone: NgZone = inject(NgZone);

    #scriptElement: HTMLScriptElement | null = null;
    #loadedUrl: string = '';

    /**
     * The CF URL built from the site identifier is the only one that serves the library —
     * the embedded URL returned by create_order comes back empty, so the caller's
     * `jsLibraryUrl` is accepted for interface parity but deliberately not used.
     */
    get #libraryUrl(): string {
        // An empty identifier builds .../api/cf//v1/paynearme.js, which 404s and then costs the
        // ROSE the full script-load timeout before the form gives up. Say so immediately.
        if (!environment.paynearme.siteIdentifier) {
            throw new Error('PayNearMe site identifier is not configured (PNM_SITE_IDENTIFIER).');
        }
        return `https://www.${environment.paynearme.host}/api/cf/${environment.paynearme.siteIdentifier}/v1/paynearme.js`;
    }

    public loadAndInit(jsLibraryUrl: string, config: PnmInitConfig): Promise<void> {
        return this.#loadLibrary().then(() => this.#init(config));
    }

    #loadLibrary(): Promise<void> {
        const url = this.#libraryUrl;

        if (typeof PNM !== 'undefined' && this.#loadedUrl === url) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            this.cleanup();

            const script = document.createElement('script');
            script.src = url;
            script.async = true;

            const timeout = setTimeout(() => {
                script.remove();
                reject(new Error('PayNearMe library load timed out.'));
            }, SCRIPT_LOAD_TIMEOUT_MS);

            script.onload = () => {
                clearTimeout(timeout);
                this.#loadedUrl = url;
                this.#waitForPnm()
                    .then(() => this.#ngZone.run(() => resolve()))
                    .catch(() => {
                        script.remove();
                        this.#ngZone.run(() => reject(new Error('PNM is not defined after load')));
                    });
            };

            script.onerror = () => {
                clearTimeout(timeout);
                script.remove();
                this.#ngZone.run(() => reject(new Error('Failed to load PayNearMe library.')));
            };

            this.#scriptElement = script;
            document.body.appendChild(script);
        });
    }

    /** The script's onload fires before `PNM` is assigned, so poll for the global. */
    #waitForPnm(): Promise<void> {
        return new Promise((resolve, reject) => {
            let elapsed = 0;
            const check = () => {
                if (typeof PNM !== 'undefined') {
                    resolve();
                } else if (elapsed >= PNM_READY_TIMEOUT_MS) {
                    reject(new Error('PNM not available after script load'));
                } else {
                    elapsed += PNM_READY_POLL_MS;
                    setTimeout(check, PNM_READY_POLL_MS);
                }
            };
            check();
        });
    }

    #init(config: PnmInitConfig): void {
        // Close any previous session and empty the container, so reopening the modal
        // always yields a blank form rather than the last attempt's state.
        this.close();
        const target = document.getElementById(config.target);
        if (target) {
            target.innerHTML = '';
        }

        PNM.init({
            order_token: config.orderToken,
            callback: (data: any) => this.#ngZone.run(() => config.callback(data)),
            target: config.target,
            auto_resize: true,
            language: config.language || 'en',
            actions: {
                [config.action.name]: {
                    action: config.action.type,
                    header: '',
                    button_text: config.buttonText || undefined,
                    ...(config.paymentTypes || { credit: true, debit: true })
                }
            }
        });

        PNM.launch(config.action.name);
    }

    /** Submits the embedded form for the given action. Safe to call in any state. */
    public submit(actionName: string): void {
        if (typeof PNM !== 'undefined' && PNM.submit) {
            PNM.submit(actionName);
        }
    }

    /** Closes the current PNM session, if any. Safe to call when PNM is not loaded. */
    public close(): void {
        if (typeof PNM !== 'undefined' && PNM.close) {
            try {
                PNM.close();
            } catch {
                // PNM throws when there is no open session; nothing to close is not an error.
            }
        }
    }

    public cleanup(): void {
        if (this.#scriptElement) {
            this.#scriptElement.remove();
            this.#scriptElement = null;
        }
    }
}
